import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail } from "lucide-react";
import { EditableText, renderInline, useContentText } from "../lib/content";
import { useSeo } from "../lib/useSeo";

const GROUPS = [
  {
    title: "Fundamentals",
    items: [
      {
        q: "What is Business Name Protection?",
        a: "Business Name Protection is an affordable, structured layer of protection for your UK business name. We record your dated proof of trading, give you plain-English guidance on the digital footprint to secure around your name, and back you with up to £10,000 of passing-off legal assistance if anything ever goes wrong.",
      },
      {
        q: "Trade marks vs Business Name Protection, a simple comparison",
        a: "Trade marks give you statutory rights in defined classes via the UK Intellectual Property Office. They cost typically £200 to £1,000+ in fees and take around 4 months. Business Name Protection costs £15 per month, activates same-day, evidences your trading history, and includes up to £10,000 passing-off legal assistance. Trade marks are powerful for product brands going national. Business Name Protection is the practical, affordable layer most UK small businesses actually need. The two can sit alongside each other.",
      },
      {
        q: "Does Companies House already protect my name?",
        a: "No. Companies House only blocks exact-duplicate registrations. It allows near-identical and confusingly-similar company names to coexist on the register. That is the gap we close.",
      },
    ],
  },
  {
    title: "Legal standing",
    items: [
      {
        q: "What is passing off?",
        a: "Passing off is a UK common-law right that protects the goodwill associated with your business name. To succeed in a claim you generally need to prove three things: goodwill in the name, misrepresentation by the other party that causes confusion, and damage. The single biggest weakness in small-business passing-off claims is patchy evidence, and we build that evidence for you over time.",
      },
      {
        q: "Will Business Name Protection give me legal standing in a dispute?",
        a: "Yes, in two ways. First, we build the structured, dated record of name use that underpins UK passing-off claims. Second, every subscription includes **up to £10,000 of passing-off legal assistance**, if a competitor copies your name, you have real backing, not just paperwork. Most disputes resolve without ever needing court action.",
      },
      {
        q: "What if I notice a competitor copying my name?",
        a: "Get in touch with us straight away. As a customer you are closest to your own market and best placed to notice issues. Once you tell us, our team helps you draft a response, escalates as needed, and applies our up-to-£10,000 passing-off legal assistance if it becomes a legal matter.",
      },
    ],
  },
  {
    title: "Plans & pricing",
    items: [
      {
        q: "How much does it cost?",
        a: "Monthly Protection: £12.50 + VAT = £15 per month. No setup fee. Annual Protection: £129.50 + VAT = £155.40 per year. Both give you the same protection, including up to £10,000 of passing-off legal assistance.",
      },
      {
        q: "How long does activation take?",
        a: "First payment processes within 24 hours and your record is created the same day. You have everything you need from day one.",
      },
    ],
  },
];

export default function FAQs() {
  const phone = useContentText("footer.contact.phone", "0800 069 9090");
  const email = useContentText("footer.contact.email", "info@nbrg.co.uk");
  const phoneDigits = phone.replace(/\D/g, "");

  useSeo({
    title: "FAQs - UK Business Name Protection",
    description: "Plain-English answers to the most common questions about Business Name Protection: how it differs from a trade mark, what passing off means, pricing and activation.",
  });

  return (
    <main data-testid="faqs-page">
      <section className="relative overflow-hidden bnp-radial-glow pt-14 md:pt-20 pb-12">
        <div className="absolute inset-0 bnp-grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center relative">
          <span className="bnp-chip bnp-chip-teal">FAQs</span>
          <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight">
            <EditableText keyName="faqs.hero.title" default="Honest answers to common questions." />
          </h1>
          <p className="text-[#447694] mt-5 leading-relaxed">
            <EditableText keyName="faqs.hero.body" default="Including the limitations we want you to know about before you subscribe." />
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 lg:py-20 xl:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 space-y-8">
          {GROUPS.map((g, gi) => (
            <div key={g.title} className="bnp-card p-6 md:p-8" data-testid={`faq-group-${gi}`}>
              <p className="text-[11px] text-[#447694] tracking-widest uppercase mb-2 font-semibold">{g.title}</p>
              <h2 className="font-display text-2xl text-[#04273d] font-semibold mb-4">{g.title}</h2>
              <Accordion type="single" collapsible className="w-full">
                {g.items.map((it, ii) => (
                  <AccordionItem key={ii} value={`g-${gi}-${ii}`} className="border-b border-[#9ac7f7]/25" data-testid={`faq-item-${gi}-${ii}`}>
                    <AccordionTrigger className="text-left text-[#04273d] hover:no-underline font-display font-semibold text-base py-4">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#447694] text-sm leading-relaxed pb-4">
                      {renderInline(it.a)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] font-bold tracking-tight">
            <EditableText keyName="faqs.cta.title" default="Question we haven't answered?" />
          </h2>
          <p className="text-[#447694] mt-3">
            <EditableText keyName="faqs.cta.body" default="Get in touch - our UK team replies within one working day. Or run a free check first." />
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3" data-testid="faqs-cta-buttons">
            <a href={`tel:${phoneDigits}`} className="btn-primary" data-testid="faqs-cta-call">
              <Phone size={14} /> {phone}
            </a>
            <a href={`mailto:${email}`} className="btn-secondary" data-testid="faqs-cta-email">
              <Mail size={14} /> Contact us
            </a>
            <Link to="/check" className="btn-secondary" data-testid="faqs-cta-check">
              Run a free check <ArrowRight size={14} />
            </Link>
          </div>
          <p className="text-xs text-[#447694] mt-4">UK phone support, Mon-Fri 9am-5pm.</p>
        </div>
      </section>
    </main>
  );
}
